import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import logoPos from "../../../assets/logos/hulahoop-logo-pos.svg";
import { GridContainer } from "./GridContainer";
import { Button } from "../ui/Button";
import { analytics } from "../../../lib/analytics";

const appUrl = import.meta.env.VITE_APP_URL || "https://app.hulahoop.pro";

const navLinks = [
  { href: "/#solucion", label: "Solución" },
  { href: "/#producto", label: "Producto" },
  { href: "/#portal", label: "Portal inversores" },
];

/**
 * Sticky navbar with blur effect.
 * Uses design tokens from tokens.css
 */
export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const handleLogoClick = () => {
    // If on landing page, scroll to top. Otherwise, navigate home.
    if (window.location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.location.href = "/";
    }
  };

  return (
    <>
      <header
        className="sticky top-0 z-50"
        style={{
          background: "var(--navbar-bg)",
          backdropFilter: "blur(var(--navbar-blur))",
        }}
      >
        <GridContainer style={{ height: "var(--navbar-height)" }}>
          <div className="col-span-4 md:col-span-8 xl:col-span-12 flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={handleLogoClick}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
              aria-label="Ir al inicio"
            >
              <img
                src={logoPos}
                alt="Hulahoop Pro"
                style={{ height: "var(--navbar-logo-height)" }}
              />
            </button>

            {/* Navigation Links - centered (desktop only) */}
            <nav className="hidden md:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Login button - hidden on mobile (shown in mobile menu) */}
              <a href={appUrl} className="hidden md:block">
                <Button variant="ghost" size="sm">
                  Iniciar sesión
                </Button>
              </a>
              <a href="/contact" onClick={() => analytics.ctaContact("navbar")}>
                <Button variant="secondary" size="sm">
                  Contactar
                </Button>
              </a>

              {/* Hamburger button - mobile only, far right */}
              <button
                className="md:hidden flex items-center justify-center w-11 h-11 text-text-primary hover:text-text-secondary transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
                aria-expanded={isMenuOpen}
              >
                <span className="text-2xl">{isMenuOpen ? "✕" : "☰"}</span>
              </button>
            </div>
          </div>
        </GridContainer>
      </header>

      {/* Full-page mobile menu overlay - rendered via portal */}
      {createPortal(
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="md:hidden"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                backgroundColor: "#ffffff",
              }}
            >
              {/* Menu header - mirrors navbar */}
              <GridContainer style={{ height: "var(--navbar-height)" }}>
                <div className="col-span-4 flex items-center justify-between">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleLogoClick();
                    }}
                    className="focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                    aria-label="Ir al inicio"
                  >
                    <img
                      src={logoPos}
                      alt="Hulahoop Pro"
                      style={{ height: "var(--navbar-logo-height)" }}
                    />
                  </button>
                  <button
                    className="flex items-center justify-center w-11 h-11 text-text-primary hover:text-text-secondary transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                    aria-label="Cerrar menú"
                  >
                    <span className="text-2xl">✕</span>
                  </button>
                </div>
              </GridContainer>

              {/* Menu content */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
                className="flex flex-col px-6 pt-4"
                style={{ height: "calc(100dvh - var(--navbar-height))" }}
              >
                <nav>
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-4 text-xl font-medium text-text-primary hover:text-text-secondary transition-colors border-b border-border-primary"
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>

                {/* Bottom CTAs - pushed to bottom */}
                <div
                  className="mt-auto flex flex-col gap-3"
                  style={{
                    paddingBottom: "max(2rem, env(safe-area-inset-bottom))",
                  }}
                >
                  <a href={appUrl} onClick={() => setIsMenuOpen(false)} className="block">
                    <Button variant="ghost" size="md" className="w-full justify-center">
                      Iniciar sesión
                    </Button>
                  </a>
                  <a
                    href="/contact"
                    onClick={() => {
                      analytics.ctaContact("mobile_menu");
                      setIsMenuOpen(false);
                    }}
                    className="block"
                  >
                    <Button variant="primary" size="md" className="w-full justify-center">
                      Contactar
                    </Button>
                  </a>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
